import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import "./FileInput.css";
import { Flex } from "../../layout";
import Text from "../typography/Text";
import Image from "../image/Image";

/**
 * Composant de champ d'entrée de fichier avec aperçu des images sélectionnées.
 *
 * @component
 * @param {Object} props - Les propriétés du composant.
 * @param {string} [props.label] - Label du champ d'upload.
 * @param {string} [props.placeholder] - Texte indicatif pour l'input.
 * @param {string} [props.width="100%"] - Largeur du composant.
 * @param {Function} props.onChange - Fonction appelée lors d'un changement de fichier.
 * @param {string} props.id - Identifiant unique pour l'input.
 * @param {string} props.name - Nom du champ d'input.
 * @param {Array} props.value - Liste des fichiers sélectionnés avec aperçu.
 * @param {boolean} [props.multiple=true] - Autorise ou non plusieurs fichiers.
 * @param {boolean} [props.disabled=false] - Désactive le champ d'input.
 * @param {string} [props.accept=".jpg,.png,.webp"] - Types de fichiers acceptés.
 * @returns {JSX.Element} Composant FileInput.
 */
function FileInput({
  label,
  placeholder = "",
  width = "100%",
  onChange,
  id,
  name,
  value,
  multiple = true,
  disabled = false,
  accept = ".jpg,.png,.webp",
  ...props
}) {
  const [isActive, setIsActive] = useState(false);
  const inputRef = useRef(null);

  /**
   * Active l'input lorsqu'on clique sur le conteneur.
   */
  const handleFocus = () => {
    if (!disabled) {
      setIsActive(true);
      inputRef.current?.focus();
    }
  };

  return (
    <label className="FileInput" style={{ width }} htmlFor="file">
      {/* Affiche le label si fourni */}
      {label && (
        <label
          htmlFor={id}
          className="label"
          onClick={handleFocus}
          role="button"
          tabIndex={0}
        >
          <Text size="sm" className="text-sm">
            {label}
          </Text>
        </label>
      )}

      {/* Zone interactive pour l'upload */}
      <Flex
        type="center"
        className={`container ${isActive ? "active" : ""}`}
        onClick={handleFocus}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === "Enter" || e.key === " ") handleFocus();
        }}
      >
        <Text size="sm" className="placeholder">
          Format en .jpg, .png ou webp
        </Text>
        <input
          ref={inputRef}
          type="file"
          id="file"
          placeholder={placeholder}
          onChange={onChange}
          name={name}
          disabled={disabled}
          accept={accept}
          className="input"
          multiple={multiple}
          {...props}
        />
      </Flex>

      {/* Affichage des aperçus */}
      <Flex gap={5} className="previews">
        {value?.map(({ preview }, index) => (
          <Image
            src={preview}
            alt={`Aperçu ${index + 1}`}
            key={index}
            className="preview"
          />
        ))}
      </Flex>
    </label>
  );
}

// Définition des PropTypes pour une validation des props
FileInput.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  width: PropTypes.string,
  onChange: PropTypes.func,
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      preview: PropTypes.string,
    })
  ),
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  accept: PropTypes.string,
};

export default FileInput;
